import { memo } from "react";
import type { ReactElement } from "react";
import { Streamdown } from "streamdown";
import cx from "ui/cx";
import styles from "./MarkdownRenderer.module.scss";

export type MarkdownMode = "static" | "streaming";

export interface MarkdownRendererProps {
  readonly children: string;
  readonly mode?: MarkdownMode;
  readonly className?: string;
}

// Uses `streamdown` (vs. `react-markdown`) because consumers stream tokens
// incrementally and need safe partial-document parsing. Static markdown would
// otherwise be fine to render with `react-markdown`.
//
// Safety: streamdown ships rehype-harden by default, which strips <script>
// tags, on* event handler attributes, and javascript:/dangerous URL schemes.
// Safe for AI-generated and user-supplied markdown. Do not bypass the renderer
// (no dangerouslySetInnerHTML on its output, no custom rehype config that
// removes harden).
export const MarkdownRenderer = memo(
  function MarkdownRenderer({ children, mode = "static", className }: MarkdownRendererProps): ReactElement {
    return (
      <Streamdown className={cx(styles.markdown, mode === "streaming" && styles.streaming, className)} mode={mode}>
        {children}
      </Streamdown>
    );
  },
  (prev, next) => prev.children === next.children && prev.mode === next.mode && prev.className === next.className,
);

MarkdownRenderer.displayName = "MarkdownRenderer";

export default MarkdownRenderer;
